<template>
  <div class="comment-wrapper">
    <div class="comment-card" :class="{ 'clickable-expand': canExpand }">
      <div class="comment-header">
        <span class="author-name" :title="content.authorDisplayName">
          {{ authorName }}
        </span>
        <span class="meta-dot">&middot;</span>
        <timeago :datetime="timestamp" :auto-update="60" class="timestamp"/>

        <button 
          v-if="hasReplies && showThread" 
          class="thread-toggle-btn" 
          @click.stop="toggleThread"
          title="Toggle replies"
        >
          {{ isThreadExpanded ? '[-]' : '[+]' }}
        </button>
      </div>

      <div class="comment-body" @click="toggleTextExpand">
        <div 
          class="text-content" 
          :class="{ 'text-truncated': isTruncated }"
          v-html="content.textDisplay"
        ></div>
        <div v-if="isTruncated" class="expand-hint">Show more</div>
      </div>

      <div class="comment-footer" v-if="hasStats">
        <div class="actions-row">
          <div class="stat-badge likes-badge" v-if="hasLikes" title="Likes">
            <svg class="action-icon heart-icon" viewBox="0 0 24 24" fill="currentColor">
               <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>
            <span class="stat-count">{{ formattedLikes }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showThread && hasReplies" class="comment-replies" v-show="isThreadExpanded">
      <comment 
        v-for="reply in replies" 
        :key="reply.id || reply.externalId" 
        :comment="reply"
        :allow-truncation="false" 
        :show-thread="true"
        :force-expand="forceExpand"
      />
    </div>
  </div>
</template>

<script>
const AppLib = require("../../../library/applib.js");

export default {
  name: "Comment", 
  props: {
    comment: { type: Object, required: true },
    allowTruncation: { type: Boolean, default: false },
    showThread: { type: Boolean, default: true }, 
    forceExpand: { type: Boolean, default: true }  
  },
  components: {
    Comment: () => import('./Comment.vue') 
  },
  data() {
    return {
      isTextExpanded: false,
      isThreadExpanded: true
    }
  },
  watch: {
    forceExpand: function(newVal) {
      this.isThreadExpanded = newVal;
    }
  },
  mounted() {
    this.isThreadExpanded = this.forceExpand;
  },
  computed: {
    // Robust access: works for wrapped {content: {...}} AND flat {...} structures
    content() {
      if (this.comment && this.comment.content) {
        return this.comment.content;
      }
      return this.comment || {};
    },
    replies() {
      return this.comment.replies || [];
    },
    hasReplies() {
      return this.replies.length > 0;
    },
    isLongContent() {
      const text = this.content.textDisplay || "";
      const stripped = text.replace(/(<([^>]+)>)/gi, "");
      return stripped.length > 120;
    },
    canExpand() {
      return this.allowTruncation && this.isLongContent;
    },
    isTruncated() {
      return this.canExpand && !this.isTextExpanded;
    },
    timestamp() {
      return this.content.publishedAt;
    },
    authorName() {
      return this.content.authorDisplayName || 'Anonymous';
    },
    currentStats() {
      const statArray = this.comment.stats || [];
      return statArray[statArray.length - 1] || {};
    },
    formattedLikes() {
      const likes = this.currentStats.likes || 0;
      return AppLib.scaleFormatNumber(likes, 1);
    },
    hasLikes() {
      return (this.currentStats.likes || 0) > 0;
    },
    hasStats() {
      return this.hasLikes;
    }
  },
  methods: {
    toggleTextExpand() {
      if (this.canExpand) this.isTextExpanded = !this.isTextExpanded;
    },
    toggleThread() {
      this.isThreadExpanded = !this.isThreadExpanded;
    }
  }
};
</script>

<style scoped>
.comment-wrapper {
  display: block;
}

.comment-card {
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif;
  background: #fff;
  position: relative; 
}
.comment-card:last-child { border-bottom: none; }

/* REPLIES STYLING */
.comment-replies {
  margin-left: 1.5rem; 
  border-left: 2px solid #e0e0e0; 
  padding-left: 1rem;
}

.comment-replies .comment-card {
  padding: 0.5rem 0;
  border-bottom: 1px solid #f9f9f9;
}

/* Header & Toggle */
.comment-header {
  display: flex; align-items: center; font-size: 0.75rem; margin-bottom: 0.3rem;
}
.author-name { font-weight: 700; color: #333; max-width: 60%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.meta-dot { margin: 0 4px; color: #ccc; }
.timestamp { color: #999; }

.thread-toggle-btn {
  background: none; border: none; padding: 0 5px;
  color: #888; font-family: monospace; font-size: 1rem;
  cursor: pointer; margin-left: auto; /* Pushes to right */
}
.thread-toggle-btn:hover { color: #333; }

/* Content Body */
.clickable-expand .comment-body { cursor: pointer; }
.clickable-expand .comment-body:hover .text-content { opacity: 0.8; }

.text-content {
  font-size: 0.85rem; line-height: 1.4; color: #444; word-wrap: break-word;
}
.text-content >>> a { color: #BA0D61; text-decoration: none; }

.text-truncated {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.expand-hint { font-size: 0.7rem; color: #BA0D61; margin-top: 2px; font-weight: 500;}

.comment-footer { margin-top: 4px; display: flex; }
.stat-badge {
  display: inline-flex; align-items: center; background: #f5f5f5;
  padding: 2px 6px; border-radius: 4px; font-size: 0.7rem; color: #666;
}
.heart-icon { width: 10px; height: 10px; margin-right: 3px; color: #ff3b30; }
</style>



// WEBPACK FOOTER //
// src/components/site/Comment.vue